import { useState, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import { toast } from 'react-hot-toast';

export default function HomePopup({ data }) {
    const [isOpen, setIsOpen] = useState(false);
    const [dontShowAgain, setDontShowAgain] = useState(false);

    useEffect(() => {
        if (!data || !data.is_active) return;

        // Check session storage
        if (!sessionStorage.getItem(`hide_home_popup_${data.id}`)) {
            // Small delay to let the page load then pop out
            const timeout = setTimeout(() => {
                setIsOpen(true);
            }, 800);
            return () => clearTimeout(timeout);
        }
    }, [data]);

    const handleClose = () => {
        setIsOpen(false);
        if (dontShowAgain) {
            sessionStorage.setItem(`hide_home_popup_${data.id}`, 'true');
        } else {
            // If they just dismiss it without checking, we could optionally track that too,
            // but the requirement was: check "don't show again" -> doesn't show until browser restart.
            // Wait, "mỗi khi load lại trang chủ là hiện popup, cho họ thêm 1 cái check 'don't show again', 
            // nó sẽ không hiển thị nữa cho đến khi đóng trình duyệt mở lại."
            // This means without checking, it ALWAYS shows up on reload.
        }
    };

    const handleCopy = () => {
        if (!data.coupon_code) return;

        navigator.clipboard.writeText(data.coupon_code).then(() => {
            toast.success('Coupon copied!');
            if (data.link_url) {
                // Short delay so user can briefly see the success message
                setTimeout(() => {
                    window.location.href = data.link_url;
                }, 600);
            }
        }).catch(() => {
            // Fallback
            toast.error('Failed to copy. Please manually select the code.');
            if (data.link_url) {
                setTimeout(() => {
                    window.location.href = data.link_url;
                }, 600);
            }
        });
    };

    if (!data || !data.is_active) return null;

    return (
        <Transition show={isOpen} className="relative z-[99999]" as="div">
            <Transition.Child
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={handleClose} />
            </Transition.Child>

            <div className="fixed inset-0 z-[100000] overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0 pointer-events-none">
                    <Transition.Child
                        enter="ease-out duration-300 transform"
                        enterFrom="opacity-0 translate-y-8 scale-95"
                        enterTo="opacity-100 translate-y-0 scale-100"
                        leave="ease-in duration-200 transform"
                        leaveFrom="opacity-100 translate-y-0 scale-100"
                        leaveTo="opacity-0 translate-y-8 scale-95"
                    >
                        <div className="relative transform overflow-hidden rounded-2xl bg-white dark:bg-[#181410] text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-xl pointer-events-auto border border-slate-200 dark:border-white/10 m-auto flex flex-col">
                            {/* Close Button */}
                            <button
                                onClick={handleClose}
                                className="absolute right-3 top-3 z-10 size-8 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 text-white backdrop-blur-md transition-colors"
                            >
                                <span className="material-symbols-outlined text-[18px]">close</span>
                            </button>

                            {/* Image Section */}
                            {data.image_url ? (
                                data.link_url ? (
                                    <a href={data.link_url} target="_blank" rel="noopener noreferrer" className="block w-full cursor-pointer relative group">
                                        <div className="w-full aspect-video bg-slate-100 dark:bg-black overflow-hidden relative">
                                            <img src={data.image_url} alt={data.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 ease-out" />
                                        </div>
                                        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
                                            <span className="bg-primary text-black font-bold px-4 py-1.5 rounded-full text-xs flex items-center gap-1 shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-transform">
                                                Learn More <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
                                            </span>
                                        </div>
                                    </a>
                                ) : (
                                    <div className="block w-full relative">
                                        <div className="w-full aspect-video bg-slate-100 dark:bg-black overflow-hidden flex items-center justify-center">
                                            <img src={data.image_url} alt={data.name} className="w-full h-full object-contain" />
                                        </div>
                                    </div>
                                )
                            ) : null}

                            {/* Description Section */}
                            {data.description && (
                                <div className="p-4 bg-primary/5 border-2 border-primary/40 dark:bg-primary/10 mx-5 mt-5 mb-1 rounded-xl shadow-inner">
                                    <p className="text-sm text-slate-800 dark:text-white/90 whitespace-pre-line leading-relaxed text-center font-medium">
                                        {data.description}
                                    </p>
                                </div>
                            )}

                            {/* Coupon Section */}
                            {(data.coupon_code || !data.image_url) && (
                                <div className={`p-6 flex flex-col items-center justify-center text-center ${!data.image_url ? 'py-12' : ''}`}>
                                    {!data.image_url && <h3 className="text-xl font-bold mb-4 text-slate-900 dark:text-white">{data.name}</h3>}
                                    
                                    {data.coupon_code && (
                                        <div className="w-full max-w-[280px]">
                                            <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Your Coupon Code</p>
                                            <div className="relative group">
                                                <div className="absolute -inset-0.5 bg-gradient-to-r from-primary to-orange-400 rounded-xl blur opacity-30 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
                                                <div className="relative flex items-stretch bg-white dark:bg-black border-2 border-primary/30 rounded-xl overflow-hidden p-1 shadow-inner">
                                                    <div className="flex-1 flex items-center justify-center py-2.5 px-3">
                                                        <span className="font-mono text-lg font-black tracking-widest text-primary">{data.coupon_code}</span>
                                                    </div>
                                                    {data.show_copy_button && (
                                                        <button
                                                            onClick={handleCopy}
                                                            className="flex items-center gap-1.5 bg-primary/10 hover:bg-primary hover:text-white px-4 shrink-0 rounded-lg text-primary font-bold transition-colors text-sm"
                                                        >
                                                            <span className="material-symbols-outlined text-[18px]">content_copy</span>
                                                            Copy
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {data.link_url && !data.image_url && (
                                        <a href={data.link_url} className="mt-6 px-6 py-3 bg-primary text-black font-bold rounded-xl shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-all hover:-translate-y-0.5">
                                            Get Offer Now
                                        </a>
                                    )}
                                </div>
                            )}

                            {/* Footer Options */}
                            <div className="px-6 py-4 border-t border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-white/5 flex items-center justify-center">
                                <label className="flex items-center gap-2 cursor-pointer group">
                                    <input 
                                        type="checkbox" 
                                        className="rounded border-slate-300 dark:border-white/20 text-primary focus:ring-primary w-4 h-4"
                                        checked={dontShowAgain}
                                        onChange={(e) => setDontShowAgain(e.target.checked)}
                                    />
                                    <span className="text-xs text-slate-600 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                                        Don't show this again
                                    </span>
                                </label>
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </div>
        </Transition>
    );
}
